import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

@Component({
  selector: 'frodo-boxes-input',
  templateUrl: './boxes-input.component.html',
  styleUrls: ['./boxes-input.component.scss'],
})
export class BoxesInputComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() allowSymbols = false;
  @Input() allowAlphanumeric = true;
  @Input() autoFocus = false;
  @Input() boxesLength: number;

  @Output() onFocusEvent = new EventEmitter<any>();
  @Output() onChangeEvent = new EventEmitter<any>();
  @Output() codeChange = new EventEmitter<string>();

  inputs = {};
  currentBoxInput: string;
  keydownHandler: (event: KeyboardEvent) => void;

  ngOnInit(): void {
    this.initInputBoxesValues();
    if (this.autoFocus) {
      this.addPhysicalKeyboardListener();
    }
  }

  ngAfterViewInit(): void {
    if (this.autoFocus) {
      this.setFocus('input0');
    }
  }

  initInputBoxesValues(): void {
    for (let index = 0; index < this.boxesLength; index++) {
      this.inputs[`input${index}`] = '';
    }
  }

  addPhysicalKeyboardListener(): void {
    this.keydownHandler = (event: KeyboardEvent) => {
      const key = event.key;
      if (key === 'Backspace') {
        event.preventDefault();
        this.handleBackspace();
      }
    };
    document.addEventListener('keydown', this.keydownHandler);
  }

  onInputFocus(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.currentBoxInput = inputElement.id;

    if (!this.autoFocus) {
      this.onFocusEvent.emit(event);
      return;
    }

    setTimeout(() => inputElement.select(), 0);
  }

  onKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  onInputChange($event): void {
    if (!this.autoFocus) {
      this.onChangeEvent.emit($event);
      return;
    }
    const inputId = `input${$event}`;
    const inputElement = document.getElementById(inputId) as HTMLInputElement;
    let value = inputElement.value;

    if (!this.allowAlphanumeric) {
      value = value.replace(/[^0-9]/g, '');
    }

    if (value.length > 1) {
      value = value.slice(-1);
    }

    inputElement.value = value;
    this.inputs[inputId] = value;

    this.handleAutoFocus($event, value);

    this.emitCode();
  }

  handleAutoFocus(index: number, value: string): void {
    if (value && index + 1 < this.boxesLength) {
      this.focusNext(index);
    }
  }

  focusNext(index: number): void {
    const nextId = index + 1;
    if (nextId < this.boxesLength) {
      this.setFocus(`input${nextId}`);
    }
  }

  handleBackspace(): void {
    const currentInput = document.getElementById(this.currentBoxInput) as HTMLInputElement;
    const currentValue = currentInput.value || '';
    const index = this.getInputIndex();

    if (currentValue) {
      currentInput.value = '';
      this.inputs[this.currentBoxInput] = '';
    } else {
      const prevId = index - 1;
      if (prevId >= 0) {
        const prevInput = `input${prevId}`;
        const prevBoxInput = document.getElementById(prevInput) as HTMLInputElement;
        if (prevBoxInput) {
          prevBoxInput.value = '';
          this.inputs[prevInput] = '';
          this.setFocus(prevInput);
        }
      }
    }

    this.emitCode();
  }

  getInputIndex(): number {
    return this.currentBoxInput.length > 6
      ? parseInt(this.currentBoxInput.slice(-2))
      : parseInt(this.currentBoxInput.slice(-1));
  }

  setFocus(id: string): void {
    this.currentBoxInput = id;
    const element = document.getElementById(id) as HTMLInputElement;
    if (element) {
      element.focus();
      setTimeout(() => element.select(), 0);
    }
  }

  emitCode(): void {
    const code = Object.keys(this.inputs)
      .sort()
      .map((key) => this.inputs[key])
      .join('');

    if (code.length === this.boxesLength) {
      this.codeChange.emit(code);
    } else {
      this.codeChange.emit('');
    }
  }

  clearInputs(): void {
    Object.keys(this.inputs).forEach((key) => {
      this.inputs[key] = '';
      const element = document.getElementById(key) as HTMLInputElement;
      if (element) {
        element.value = '';
      }
    });
    if (this.autoFocus && this.boxesLength > 0) {
      this.setFocus('input0');
    }
    this.emitCode();
  }

  ngOnDestroy(): void {
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = undefined;
    }
  }
}
